import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    // If there's a variant in the URL on page load, select it
    this.#selectVariantFromUrl();
    this.#autoSelectFirstAvailableOption();
    document.addEventListener('quick-add::opened', ()=> {
      this.setFirstAvailableVariantActiveOnFocus();
    })

    if (!this.hasAttribute('data-variant-loading')) return;

    // If no fetch was triggered (e.g., product has only default variant), reveal immediately
    if (!this.#abortController) {
      this.removeAttribute('data-variant-loading');
    }
  }

  /**
   * Selects variant if there's a variant parameter in the URL
   */
  #selectVariantFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    if (variantId) {
      // Find ALL options with this variant ID (for multi-option products like Color + Size)
      const optionsWithVariant = this.querySelectorAll(`[data-variant-id="${variantId}"]`);

      if (optionsWithVariant.length > 0) {
        // Select all options that match this variant ID (e.g., both Color and Size)
        optionsWithVariant.forEach((option) => {
          this.updateSelectedOption(option);
        });

        // Use the last option for triggering events (most specific selection)
        const lastOption = optionsWithVariant[optionsWithVariant.length - 1];

        // Trigger the variant selected and update events so button state updates
        const optionValueId = lastOption.dataset.optionValueId;
        if (optionValueId) {
          this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId }));

          // Fetch updated section to trigger button state update
          this.fetchUpdatedSection(this.buildRequestUrl(lastOption), false);
        }
      } else {
        // Variant not found in DOM (possibly hidden via hide_variant metafield).
        // Clear the URL param so auto-select can pick the first visible variant.
        const url = new URL(window.location.href);
        url.searchParams.delete('variant');
        history.replaceState({}, '', url.toString());
      }
    }
  }

  /**
   * Auto-selects the first input in each variant-option fieldset if no variant is in URL
   */
  #autoSelectFirstAvailableOption() {
    // Skip auto-selection for product cards in search/collection pages —
    // the server already rendered the correct variant based on the query.
    if (this.closest('product-card')) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('variant')) return;

    const fieldsets = this.querySelectorAll('fieldset.variant-option');
    if (fieldsets.length === 0) return;

    let lastSelectedInput = null;

    fieldsets.forEach((fieldset) => {
      const firstInput = fieldset.querySelector('input[data-option-value-id]');
      if (!firstInput || !(firstInput instanceof HTMLInputElement)) return;
      if (firstInput.checked) return;

      firstInput.checked = true;
      lastSelectedInput = firstInput;
    });

    if (lastSelectedInput) {
      this.dispatchEvent(new VariantSelectedEvent({ id: lastSelectedInput.dataset.optionValueId ?? '' }));
      this.fetchUpdatedSection(this.buildRequestUrl(lastSelectedInput), false);
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;   
    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) return false;

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
        
        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }

        this.removeAttribute('data-variant-loading');
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          this.removeAttribute('data-variant-loading');
          console.error(error);
        }
      });
  }

  setFirstAvailableVariantActiveOnFocus() {
    const variants = this.querySelectorAll('input[data-option-value-id]');
    const colorOptions = this.querySelectorAll('[data-color]');
    const sizeOptions = this.querySelectorAll('[data-size]');
    if(colorOptions.length > 0) {
      if(!colorOptions[0]?.getAttribute('aria-disabled')) {
        colorOptions[0].dispatchEvent(new Event('focusin'));
        colorOptions[0]?.addEventListener('focusin', (event)=>  {
          const addToCartButton = document.querySelector(`product-form-component[data-section-id="${this.dataset.sectionId}"] [ref="addToCartButton"]`);
          if(addToCartButton && addToCartButton.disabled) {
            this.variantChanged(event);
          }
        })
      }
    }

    if(sizeOptions.length > 0) {
      if(!sizeOptions[0]?.getAttribute('aria-disabled')) {
        sizeOptions[0].dispatchEvent(new Event('focusin'));
        sizeOptions[0]?.addEventListener('focusin', (event)=>  {
          const addToCartButton = document.querySelector(`product-form-component[data-section-id="${this.dataset.sectionId}"] [ref="addToCartButton"]`);
          if(addToCartButton && addToCartButton.disabled) {
            this.variantChanged(event);
          }
        })
      }
    }
    if(variants.length > 0) {
      variants.forEach((variant) => {
        variant?.addEventListener("keydown", (event) => {
					const addToCartButton = document.querySelector(
						`product-form-component[data-section-id="${this.dataset.sectionId}"] [ref="addToCartButton"]`,
					);

					if (
						event.code == "Tab" && addToCartButton &&
						addToCartButton.disabled && variant.hasAttribute("aria-disabled") &&
						!variant.getAttribute("aria-disabled")
					) {
            event.preventDefault();
						const quickAddModal = this.closest(".quick-add-modal");
						if (quickAddModal) {
							const closeButton = quickAddModal.querySelector('[ref="closeButton"]');
							if (closeButton) closeButton.focus();
						}
					}
				});
			});
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Store ALL currently selected option IDs before morphing (for multi-option products)
    const currentlySelectedOptionIds = this.selectedOptionsValues;

    morph(this, newVariantPickerSource);

    // Re-apply ALL selections after morphing since Liquid doesn't add checked/selected attributes
    let selectionLost = false;
    if (currentlySelectedOptionIds.length > 0) {
      currentlySelectedOptionIds.forEach((optionId) => {
        if (this.updateSelectedOption(optionId) === false) {
          selectionLost = true;
        }
      });
    }

    // A previously selected option (e.g., size) was removed because the variant is hidden.
    // Clear the URL variant param and re-fetch to update buy buttons with current selection.
    if (selectionLost) {
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      history.replaceState({}, '', url.toString());

      const lastSelected = this.selectedOption;
      if (lastSelected) {
        this.fetchUpdatedSection(this.buildRequestUrl(lastSelected), false);
      }
    }

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
